import * as React from 'react';
import { useIntl } from 'react-intl';
import { useModal } from '@/features/modal/useModal';
import { useSelector } from 'react-redux';
import {
  selectHeroReturningUserWelcomeTextsHtml,
  selectHeroWelcomeTextsHtml,
} from '@/store/cms/welcome-texts/selectors';
import { useRegistration } from '@/hooks/useRegistration';
import classNames from 'classnames';
import { MODALS } from '@/features/modal/types';
import { Button } from '@/components/buttons/Button';
import { OpenedFrom } from '@/lib/gTagManager';
import { CDN_BASE, CDN_IMAGES_COMMON_PATH } from '@/constants/constants';
import StorageManager from '@/shared/storageManager';
import { useLogin } from '@/hooks/useLogin';
import { selectLocale } from '@/store/selectors/commonSelectors';

const AuthenticationIntro = () => {
  const intl = useIntl();
  const { openModal } = useModal();
  const welcomeTextHeroHtml = useSelector(selectHeroWelcomeTextsHtml);
  const welcomeTextHeroReturningUserHtml = useSelector(selectHeroReturningUserWelcomeTextsHtml);
  const { openRegistration } = useRegistration();
  const { openLogin } = useLogin();
  const locale = useSelector(selectLocale);
  const isReturningUser = StorageManager.getIsReturningUser();
  const text =
    isReturningUser && !!welcomeTextHeroReturningUserHtml
      ? welcomeTextHeroReturningUserHtml
      : welcomeTextHeroHtml;

  const sharedTextStyles = classNames(
    '[&_h1]:text-4xl [&_h1]:sm:text-5xl [&_h1]:md:text-6xl [&_h1]:text-white [&_h1]:text-center [&_h1]:m-0 [&_h1]:mb-[30px]',
    '[&_h2]:text-white [&_h2]:m-0 [&_h2]:mb-4 [&_h2]:text-center [&_h2]:text-2xl [&_h2]:sm:text-3xl [&_h2]:md:text-4xl [&_h2]:sm:mb-8',
    '[&_h3]:text-white [&_h3]:text-center [&_h3]:m-0 [&_h3]:mb-4 [&_h3]:font-[bolder] [&_h3]:text-[18px] [&_h3]:sm:text-[22px]',
    '[&_strong]:text-blue-lighter',
    '[&_p:empty]:hidden',
  );

  return (
    <div className="flex flex-col items-center p-[16px] md:p-0 md:mt-[76px] mt-12 gap-y-1 md:gap-y-3">
      {isReturningUser ? (
        <div
          data-cy="authentication-canada-registration-intro"
          className={classNames(
            'max-w-[900px] flex flex-col items-center',
            '[&_p]:text-white [&_p]:max-w-[700px] [&_p]:m-0 [&_p]:mb-4',
            sharedTextStyles,
          )}
        >
          {text}

          <div className="flex flex-col mt-2 max-w-[300px] w-full">
            <Button
              data-cy="authentication-login-intro-button"
              type="button"
              text={intl.formatMessage({ id: 'header.login' })}
              onClickHandler={() => openLogin({ openedFrom: OpenedFrom.ToRemoveFromRegistration })}
            />
          </div>
        </div>
      ) : (
        <div
          data-cy="authentication-canada-registration-intro"
          className={classNames(
            'max-w-[900px] text-center',
            '[&_p]:text-white [&_p]:text-xs [&_p]:max-w-[700px] [&_p]:mx-auto [&_p]:m-0 [&_p:last-of-type]:mb-0 [&_p:last-of-type]:contents',
            sharedTextStyles,
          )}
        >
          <div style={{ lineHeight: '15px' }}>
            {text}{' '}
            <button
              type="button"
              onClick={() => openModal(MODALS.BONUS_TERMS_AND_CONDITIONS)}
              className="italic underline text-white text-xs"
            >
              {intl.formatMessage({
                id: 'authentication.registration.canada.description.terms-and-conditions',
              })}
            </button>
          </div>
          <div className="flex flex-col mt-4 max-w-[300px] w-full mx-auto">
            <Button
              data-cy="authentication-registration-intro-button"
              type="button"
              text={intl.formatMessage({ id: 'header.register' })}
              onClickHandler={() =>
                openRegistration({ openedFrom: OpenedFrom.ToRemoveFromRegistration })
              }
            />
          </div>
        </div>
      )}

      <div className="flex items-center justify-center gap-4">
        <a
          href={locale === 'ca-fr' ? 'https://garde-jeu.ca/' : 'https://betguard.ca/'}
          target="_blank"
          rel="noopener noreferrer"
        >
          <img
            src={`${CDN_BASE}/cdn-cgi/image/fit=cover,width=200,height=auto,format=auto,dpr=2${CDN_IMAGES_COMMON_PATH}/footer/${
              locale === 'ca-fr' ? 'L_GRADEJEU_reverse_F_RGB.png' : 'L_BETGUARD_reverse_E_RGB.png'
            }`}
            alt={locale === 'ca-fr' ? 'Garde-jeu' : 'BetGuard'}
            className="max-w-[120px]"
          />
        </a>
        <img
          src={`${CDN_BASE}/cdn-cgi/image/fit=cover,width=180,height=auto,format=auto,dpr=2${CDN_IMAGES_COMMON_PATH}/footer/igamingontario.png`}
          alt="iGaming Ontario"
          className="max-w-[120px]"
        />
      </div>
    </div>
  );
};

export default AuthenticationIntro;
